import React, { useEffect, useMemo, useState } from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { hasAccess } from '../utils/permissions';
import { API_BASE_URL } from '../utils/config';
import { PageShell, PageHeader, Surface, MetricCard, StatePanel, Empty } from '../components/ui';
import { tokens } from '../theme';
import {
    RANGES, buildJourneyGraph, rankDropOffs, layoutJourney, bandPath, dropPath, stageLabel, pct, fmtInt,
} from './journeyGraph';

// What the funnel cannot see yet. Rendered on the page (and listed in the PR)
// so nobody reads an empty column as "zero traffic".
export const NOT_TRACKED = [
    'Traffic source — no UTM, referrer or install-referrer is stored on users or activity logs, and skilz-landing has no analytics tag. The App Store / Play links carry no campaign parameters.',
    'Pre-login screens — the app only sends PAGE_VIEW after login (usePageTracking needs a token), so onboarding slides, phone entry and OTP screens are not recorded.',
    'Phone entered / OTP requested — no server event before the OTP is verified; the first event is REGISTER (account created).',
    'Landing site & skilz-web page views — no page-view tracking outside the mobile app.',
];

const GREEN = tokens.primary;
const INK = tokens.ink;
const MUTED = tokens.muted;
const GREY = '#8a8f8b';

const truncate = (s, n = 22) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

const JourneySankey = ({ graph }) => {
    const L = useMemo(() => layoutJourney(graph), [graph]);
    const { byId, nodeWidth: NW, colWidth: CW } = L;
    const seamX = 24 + 3 * CW - 26;
    return (
        <div className="sk-table-wrap" style={{ overflowX: 'auto' }}>
            <svg viewBox={`0 0 ${L.width} ${L.height}`} width={L.width} height={L.height} role="img"
                aria-label="User journey Sankey: traffic source to landing screen to next screen, then register to paid"
                style={{ display: 'block', font: '10.5px Inter, system-ui, sans-serif' }}>
                {Array.from({ length: L.cols }, (_, i) => (
                    <text key={i} x={24 + i * CW} y={12} fill={MUTED} style={{ letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 700 }}>{stageLabel(i)}</text>
                ))}
                <line x1={seamX} x2={seamX} y1={18} y2={L.height - 14} stroke={INK} strokeDasharray="2 4" />
                <text x={seamX + 4} y={L.height - 2} fill={MUTED}>app sessions | users (different units)</text>

                {graph.links.map(l => {
                    const s = byId.get(l.s); const t = byId.get(l.t); const w = l.w;
                    return (
                        <path key={`${l.s}>${l.t}`} d={bandPath(s.x + NW, l.sy, t.x, l.ty, w)}
                            fill={l.untracked ? GREY : GREEN} fillOpacity={l.untracked ? 0.18 : 0.32}>
                            <title>{`${s.label} → ${t.label}: ${fmtInt(l.v)} ${t.unit} (${pct(l.v, s.value)} of ${s.label})`}</title>
                        </path>
                    );
                })}

                {graph.nodes.filter(n => n.drop > 0).map(n => (
                    <g key={`drop-${n.id}`}>
                        <path d={dropPath(n.x + NW, n.dropY, n.dropH)} fill={GREY} fillOpacity={0.35}>
                            <title>{`${n.label}: ${fmtInt(n.drop)} of ${fmtInt(n.value)} ${n.unit} dropped (${pct(n.drop, n.value)})`}</title>
                        </path>
                        <text x={n.x + NW + 50} y={n.dropY + 12 + n.dropH / 2 + 3.5} fill={MUTED}>{`−${fmtInt(n.drop)} (${pct(n.drop, n.value)})`}</text>
                    </g>
                ))}

                {graph.nodes.map(n => (
                    <g key={n.id}>
                        <rect x={n.x} y={n.y} width={NW} height={n.h}
                            fill={n.kind === 'untracked' ? 'none' : n.kind === 'step' ? INK : GREEN}
                            stroke={n.kind === 'untracked' ? GREY : 'none'} strokeDasharray={n.kind === 'untracked' ? '3 2' : undefined}>
                            <title>{`${n.label}\n${fmtInt(n.value)} ${n.unit}${n.drop ? `\ndropped: ${fmtInt(n.drop)} (${pct(n.drop, n.value)})` : ''}`}</title>
                        </rect>
                        <text x={n.x - 2} y={n.y - 4} fill={n.kind === 'untracked' ? MUTED : INK} fontWeight={600}>
                            {truncate(n.label)} <tspan fill={MUTED} fontWeight={400}>{fmtInt(n.value)}</tspan>
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
};

export default function UserJourney() {
    const { permissions, loaded } = usePermissions();
    const roles = (permissions && permissions.adminRoles) || [];
    const allowed = loaded && hasAccess('user-journey', roles);
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!allowed) return undefined;
        let alive = true;
        setLoading(true);
        setError(null);
        httpClient(`${API_BASE_URL}/admin/journey?days=${days}`, { method: 'GET', cache: 'no-store' })
            .then(({ json }) => { if (alive) setData(json); })
            .catch(e => { if (alive) setError(e && e.status === 403 ? 'You do not have access to journey analytics.' : 'Could not load journey data.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [days, allowed]);

    const graph = useMemo(() => buildJourneyGraph(data), [data]);
    const drops = useMemo(() => rankDropOffs(graph), [graph]);
    const steps = (data && data.funnel && data.funnel.steps) || [];
    const independent = (data && data.funnel && data.funnel.independent) || {};
    const sessions = (data && data.sessions) || {};
    const registered = steps.length ? steps[0].count : 0;
    const paid = steps.length ? steps[steps.length - 1].count : 0;

    if (!loaded) return <PageShell><StatePanel state="loading">Loading permissions…</StatePanel></PageShell>;
    if (!hasAccess('user-journey', roles)) return <PageShell><StatePanel state="error">You do not have permission to view this page.</StatePanel></PageShell>;

    const rangeTabs = (
        <div className="sk-dashboard-chart-tabs" role="group" aria-label="Journey date range">
            {RANGES.map(d => (
                <button key={d} type="button" aria-pressed={days === d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>{d} days</button>
            ))}
        </div>
    );

    return (
        <main className="sk-page">
            <PageHeader eyebrow="Insights" title="User Journey"
                description="Traffic source → landing screen → next screen → register → signup steps → paid, with the drop-off at every stage."
                actions={rangeTabs} />

            {loading && <StatePanel state="loading">Loading journey…</StatePanel>}
            {error && <StatePanel state="error">{error}</StatePanel>}

            {data && (
                <>
                    <div className="sk-metric-grid" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                        <MetricCard label="App sessions" value={fmtInt(sessions.total)} detail="Logged-in sessions with a page view" />
                        <MetricCard tone="blue" label="Registered" value={fmtInt(registered)} detail={`Accounts created, last ${days} days`} />
                        <MetricCard tone="purple" label="KYC L1 (any)" value={fmtInt(independent.kycL1)} detail={`L2: ${fmtInt(independent.kycL2)}`} />
                        <MetricCard tone="orange" label="Paid" value={fmtInt(paid)} detail={`Register → paid ${pct(paid, registered)} (sequential)`} />
                    </div>

                    <Surface title="Journey" description={`Last ${days} days · hover any band or node for numbers · grey stubs are drop-offs`}>
                        {!graph.hasSessions && (
                            <StatePanel state="empty">No app page views in this window — the landing / next-screen columns are empty. The signup funnel below is still live.</StatePanel>
                        )}
                        {graph.nodes.length ? <JourneySankey graph={graph} /> : <Empty>No data in this window.</Empty>}
                        <p className="sk-muted" style={{ marginTop: 12 }}>
                            <strong>Two units, one seam.</strong> Left of the dotted line: logged-in app sessions (first screen → second screen).
                            Right of it: users who created an account in the same window, counted sequentially (a user counts at a step only if they reached every earlier step).
                            The two sides are not joined by a measured transition. Traffic source is drawn as a single dashed “not tracked” node.
                        </p>
                    </Surface>

                    <Surface title="Biggest drop-offs" description="Ranked by number of people lost at that stage">
                        {drops.length ? (
                            <div className="sk-table-wrap">
                                <table className="sk-data-table">
                                    <thead><tr><th>#</th><th>Stage</th><th>Node</th><th style={{ textAlign: 'right' }}>Reached</th><th style={{ textAlign: 'right' }}>Dropped</th><th style={{ textAlign: 'right' }}>Drop %</th><th>Unit</th><th>Method</th></tr></thead>
                                    <tbody>
                                        {drops.map((d, i) => (
                                            <tr key={d.id}>
                                                <td>{i + 1}</td><td>{d.stage}</td><td>{d.label}</td>
                                                <td style={{ textAlign: 'right' }}>{fmtInt(d.reached)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtInt(d.dropped)}</td>
                                                <td style={{ textAlign: 'right' }}>{d.rate}</td>
                                                <td>{d.unit}</td><td className="sk-muted">{d.method}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : <Empty>No drop-offs in this window.</Empty>}
                    </Surface>

                    <Surface title="Signup funnel" description={data.funnel && data.funnel.cohort}>
                        <div className="sk-table-wrap">
                            <table className="sk-data-table">
                                <thead><tr><th>Step</th><th style={{ textAlign: 'right' }}>Users</th><th style={{ textAlign: 'right' }}>From previous</th><th style={{ textAlign: 'right' }}>From registered</th></tr></thead>
                                <tbody>
                                    {steps.map((s, i) => (
                                        <tr key={s.id}>
                                            <td>{s.label}</td>
                                            <td style={{ textAlign: 'right' }}>{fmtInt(s.count)}</td>
                                            <td style={{ textAlign: 'right' }}>{i ? pct(s.count, steps[i - 1].count) : '—'}</td>
                                            <td style={{ textAlign: 'right' }}>{pct(s.count, registered)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="sk-muted" style={{ marginTop: 10 }}>
                            {data.funnel && data.funnel.paidDefinition} Independent counts (not sequential): paid {fmtInt(independent.paid)} · paid subscription {fmtInt(independent.paidSubscription)} · wallet top-up {fmtInt(independent.walletTopUp)} · wallet booking {fmtInt(independent.walletBooking)} · KYC L2 {fmtInt(independent.kycL2)} (optional, tax exemption — not a funnel step).
                        </p>
                    </Surface>

                    <Surface title="Not tracked yet" description="Stages that have no data source today — shown as gaps, never estimated">
                        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6, fontSize: 13 }}>
                            {NOT_TRACKED.map(t => <li key={t}>{t}</li>)}
                        </ul>
                    </Surface>
                </>
            )}
        </main>
    );
}
